/**
 * Accept Invite Page
 *
 * The flow an invited member actually walks:
 * 1. Read the token from ?token=xxx
 * 2. Fetch the public preview — who invited them, to what, and whether an
 *    Auth0 account already exists for that address
 * 3. Show that, then send them to the RIGHT Auth0 screen: Create Account for
 *    a first-timer (prefilled), Log in for a returning one
 * 4. Back here authenticated, POST /auth/invites/{token}/accept
 * 5. Refresh /auth/me so the member's configured home resolves, then land
 *
 * Two defects this page used to carry (CUR-1394):
 * - It called loginWithRedirect() with no hints, so a first-time invitee was
 *   handed a Log in form with an empty email box — unfinishable, and it
 *   pushed people toward signing up separately, which is the account
 *   collision trap.
 * - Auto-join consumes the invite ~60ms after Auth0 creates the account, so
 *   the accept POST returned 409 and the SUCCESS case rendered as a red
 *   error card. The backend now returns `already_accepted`; this page keeps
 *   a 409 fallback only for a genuinely foreign invite.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { useAcceptInvite } from '@/hooks/api/usePortalOnboarding';
import { resolvePortalHome } from '@/hooks/portal/usePortalHome';
import { setStoredUserFields } from '@/services/auth/stored-user';
import { config } from '@/utils/env';

type InviteStatus = 'loading' | 'invited' | 'accepting' | 'success' | 'error';

interface ErrorInfo {
  title: string;
  message: string;
}

interface InvitePreview {
  email: string;
  organization_name: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired';
  account_exists: boolean | null;
}

// Matches the transactional email templates and the Auth0 screens this page
// hands off to, so the three surfaces read as one product.
const BRAND = {
  olive: '#4d5a2a',
  oliveMid: '#6b7a3f',
  cream: '#f1efe8',
  bone: '#f7f6f2',
  ink: '#18181a',
  body: '#3d3d38',
  muted: '#6b6b63',
  rule: '#e3ddcd',
  danger: '#a32d2d',
} as const;

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

function getErrorInfo(error: Error): ErrorInfo {
  const msg = error.message || '';

  if (msg.includes('404')) {
    return {
      title: 'Invalid Invitation',
      message: 'This invitation link is invalid or does not exist. Please ask your team admin to send a new invite.',
    };
  }
  if (msg.includes('403')) {
    return {
      title: 'Wrong Account',
      message:
        'This invitation was issued to a different email address. Sign out and sign back in with the address the invitation was sent to.',
    };
  }
  if (msg.includes('409')) {
    return {
      title: 'Already Accepted',
      message: 'This invitation has already been used by another account. Please ask your team admin to send a new invite.',
    };
  }
  if (msg.includes('410')) {
    return {
      title: 'Invitation Expired',
      message: 'This invitation has expired. Please ask your team admin to send a new invite.',
    };
  }

  return {
    title: 'Something Went Wrong',
    message: msg || 'Failed to accept the invitation. Please try again or contact support.',
  };
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const { isAuthenticated, isLoading: authLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const acceptInvite = useAcceptInvite();

  const [status, setStatus] = useState<InviteStatus>('loading');
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const hasAttempted = useRef(false);
  const hasPreviewed = useRef(false);

  // No token in URL — show error immediately
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorInfo({
        title: 'Missing Invitation Token',
        message: 'No invitation token found in the URL. Please use the link from your invitation email.',
      });
    }
  }, [token]);

  // Preview runs before authentication — it is what lets us name the org and
  // pick the right Auth0 screen instead of guessing.
  useEffect(() => {
    if (!token || hasPreviewed.current) return;
    hasPreviewed.current = true;

    (async () => {
      try {
        const res = await fetch(`${config.apiUrl}/auth/invites/${token}/preview`);
        if (!res.ok) {
          // 404 is the only preview failure worth stopping for; anything else
          // (429, 5xx) falls through to the unhinted flow rather than blocking
          // someone from joining.
          if (res.status === 404) {
            setStatus('error');
            setErrorInfo(getErrorInfo(new Error('404')));
            return;
          }
          setPreview(null);
        } else {
          const data: InvitePreview = await res.json();
          if (data.status === 'expired') {
            setStatus('error');
            setErrorInfo(getErrorInfo(new Error('410')));
            return;
          }
          setPreview(data);
        }
      } catch {
        // Network failure — proceed unhinted rather than dead-ending.
        setPreview(null);
      }

      // An already-authenticated user goes straight through; the accept call
      // is idempotent for them.
      setStatus((s) => (s === 'error' ? s : 'invited'));
    })();
  }, [token]);

  const goToAuth0 = useCallback(() => {
    if (!token) return;
    // account_exists === false means Auth0 has never seen this address, so
    // send them to Create Account. null means the Management API couldn't
    // answer — let Auth0 decide rather than guess wrong.
    const screenHint = preview?.account_exists === false ? 'signup' : 'login';
    loginWithRedirect({
      authorizationParams: {
        screen_hint: screenHint,
        ...(preview?.email ? { login_hint: preview.email } : {}),
      },
      appState: {
        returnTo: `/auth/accept-invite?token=${token}`,
      },
    });
  }, [token, preview, loginWithRedirect]);

  // Authenticated path: accept the invite (only once)
  useEffect(() => {
    if (!token || authLoading || !isAuthenticated) return;
    if (status === 'error') return;
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    setStatus('accepting');
    acceptInvite.mutate(token, {
      onSuccess: async (data) => {
        // Update localStorage with new org context
        setStoredUserFields({
          organizationId: data.organization_id,
          organizationRole: data.role,
        });

        // The /auth/me call in CallbackPage ran before this invite was
        // accepted, so the user had no org and portal_member_experience came
        // back null. Re-fetch now that membership exists — otherwise a member
        // lands on the dashboard with the full nav until their next sign-in.
        let home = '/portal/dashboard';
        try {
          const accessToken = await getAccessTokenSilently();
          const meRes = await fetch(`${config.apiUrl}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (meRes.ok) {
            const me = await meRes.json();
            setStoredUserFields({
              organizationId: data.organization_id,
              organizationRole: data.role,
              portalMemberExperience: me.portal_member_experience || undefined,
            });
            const acceptedRole = me.organization_role || data.role;
            const isOrgAdmin = acceptedRole === 'owner' || acceptedRole === 'admin';
            home = resolvePortalHome(me.portal_member_experience, isOrgAdmin);
          }
        } catch {
          // Non-fatal: the user is a member either way. They get the default
          // landing now and the configured one on next sign-in.
        }

        setStatus('success');

        // Brief pause to show success, then redirect
        setTimeout(() => {
          navigate(home, { replace: true });
        }, 1500);
      },
      onError: (err) => {
        setStatus('error');
        setErrorInfo(getErrorInfo(err instanceof Error ? err : new Error(String(err))));
      },
    });
  }, [token, authLoading, isAuthenticated, status, acceptInvite, navigate, getAccessTokenSilently]);

  const orgName = preview?.organization_name ?? 'your team';
  const roleLabel = preview?.role ? (ROLE_LABELS[preview.role] ?? preview.role) : null;
  const isNewAccount = preview?.account_exists === false;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backgroundColor: BRAND.cream,
        color: BRAND.body,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          textAlign: 'center',
          backgroundColor: BRAND.bone,
          border: `1px solid ${BRAND.rule}`,
          borderRadius: '10px',
          padding: '2.5rem 2rem',
        }}
      >
        <p
          style={{
            fontSize: '0.6875rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: BRAND.oliveMid,
            margin: '0 0 0.5rem',
          }}
        >
          CurAItion
        </p>

        {status === 'loading' && (
          <>
            <Loader2
              style={{ width: '2rem', height: '2rem', margin: '0.5rem auto 1rem', animation: 'spin 1s linear infinite', color: BRAND.olive }}
            />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', color: BRAND.ink }}>Checking your invitation…</h2>
          </>
        )}

        {status === 'invited' && !isAuthenticated && (
          <>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '1.5rem', fontWeight: 500, margin: '0 0 0.75rem', color: BRAND.ink }}>
              You've been invited to join {orgName}
            </h2>
            <p style={{ color: BRAND.muted, marginBottom: '0.25rem' }}>
              {preview?.email ? <strong style={{ color: BRAND.body }}>{preview.email}</strong> : 'Your invitation'}
              {roleLabel ? ` · ${roleLabel}` : ''}
            </p>
            <p style={{ color: BRAND.muted, fontSize: '0.9375rem', margin: '1rem 0 1.75rem' }}>
              {isNewAccount
                ? "It takes about a minute. You'll set a password, then land straight in the workspace."
                : 'Sign in to accept and land straight in the workspace.'}
            </p>
            <button
              type="button"
              onClick={goToAuth0}
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: BRAND.olive,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '1rem',
                width: '100%',
              }}
            >
              {isNewAccount ? 'Create your account' : 'Sign in to accept'}
            </button>
            <p style={{ color: BRAND.muted, fontSize: '0.8125rem', margin: '1.25rem 0 0' }}>
              Use this button rather than signing up separately — it is what connects your account to {orgName}.
            </p>
          </>
        )}

        {(status === 'accepting' || (status === 'invited' && isAuthenticated)) && (
          <>
            <Loader2
              style={{ width: '2rem', height: '2rem', margin: '0.5rem auto 1rem', animation: 'spin 1s linear infinite', color: BRAND.olive }}
            />
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', color: BRAND.ink }}>Joining {orgName}…</h2>
            <p style={{ color: BRAND.muted }}>Setting up your account access.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 style={{ width: '2.5rem', height: '2.5rem', margin: '0.5rem auto 1rem', color: BRAND.olive }} />
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '1.5rem', fontWeight: 500, marginBottom: '0.5rem', color: BRAND.ink }}>
              Welcome to the team
            </h2>
            <p style={{ color: BRAND.muted }}>Taking you to your workspace…</p>
          </>
        )}

        {status === 'error' && errorInfo && (
          <>
            {errorInfo.title === 'Already Accepted' ? (
              <AlertTriangle style={{ width: '2.5rem', height: '2.5rem', margin: '0.5rem auto 1rem', color: '#b07000' }} />
            ) : (
              <XCircle style={{ width: '2.5rem', height: '2.5rem', margin: '0.5rem auto 1rem', color: BRAND.danger }} />
            )}
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem', color: BRAND.ink }}>{errorInfo.title}</h2>
            <p style={{ color: BRAND.muted, marginBottom: '1.75rem' }}>{errorInfo.message}</p>
            <button
              type="button"
              onClick={() => navigate('/signin', { replace: true })}
              style={{
                padding: '0.875rem 1.75rem',
                backgroundColor: BRAND.olive,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                width: '100%',
              }}
            >
              Go to Sign In
            </button>
          </>
        )}
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
